import { useState } from "react";
import Input from "../../../components/input";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  Link,
} from "react-router-dom";

import {
  listUserProjects,
  userAuthenticate,
  setAuthenticatedSession,
} from "../../../hooks/local/userReducer";

import Button from "../../../components/button";
import useReveal from "../../../hooks/useReveal";
import { apiEndPoints } from "../../../hooks/remote/apiEndPoints";
import { toast } from "react-toastify";

const SignIn = () => {
  document.title = "Sign in - CloudVantage";

  const [formRef, formVisible] = useReveal();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const loading = useSelector(
    (state) => state.user.loading
  );

  /*
   * ============================
   * 2FA STATE
   * ============================
   */

  const [requiresOtp, setRequiresOtp] = useState(false);

  const [otpCode, setOtpCode] = useState("");

  const [otpEmail, setOtpEmail] = useState("");

  const [otpMethod, setOtpMethod] = useState("");

  const [otpLoading, setOtpLoading] = useState(false);

  const [resendLoading, setResendLoading] = useState(false);

  const [pendingRedirect, setPendingRedirect] = useState("/client-area");

  /*
   * ============================
   * COMPLETE LOGIN
   * ============================
   */

  const completeLogin = async (
    redirectPath = "/client-area"
  ) => {
    await dispatch(listUserProjects());

    navigate(redirectPath);
  };

  /*
   * ============================
   * VERIFY OTP
   * ============================
   */

const handleVerifyOtp = async (e) => {
  e.preventDefault();

  const code = otpCode.trim();

  if (!code) {
    toast.error("Please enter the verification code");
    return;
  }

  if (!otpEmail) {
    toast.error(
      "Unable to determine the email address for OTP verification."
    );
    return;
  }

  try {
    setOtpLoading(true);

    const response = await apiEndPoints.verifyLoginOtp({
      email: otpEmail,
      otp: code,
    });

    const data =
      response?.data?.data ||
      response?.data ||
      response;

    // Backend returned an unsuccessful OTP response
    if (
      data?.statuscode &&
      data.statuscode !== "00"
    ) {
      toast.error(
        data?.statusmessage ||
          data?.message ||
          "Invalid verification code"
      );

      // Stay on OTP screen
      setRequiresOtp(true);

      // Clear only the wrong OTP
      setOtpCode("");

      return;
    }

    /*
     * OTP VERIFIED SUCCESSFULLY
     *
     * Only at this point should we establish
     * the authenticated session.
     */
    dispatch(setAuthenticatedSession(data));

    toast.success(
      data?.statusmessage ||
        data?.message ||
        "Verification successful"
    );

    setOtpCode("");
    setRequiresOtp(false);

    await completeLogin(pendingRedirect);
  } catch (error) {
    console.error(
      "OTP verification error:",
      error
    );

    const errorMessage =
      error?.response?.data?.statusmessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Invalid verification code";

    toast.error(errorMessage);

    /*
     * IMPORTANT:
     * A failed OTP must NOT end the login flow.
     * Keep the email, OTP method and redirect
     * information intact.
     */
    setRequiresOtp(true);

    // Clear only the invalid OTP
    setOtpCode("");
  } finally {
    setOtpLoading(false);
  }
};

  /*
   * ============================
   * RESEND OTP
   * ============================
   */

  const handleResendOtp = async () => {
    try {
      setResendLoading(true);

      const response =
        await apiEndPoints.resendLoginOtp();

      const data =
        response?.data?.data ||
        response?.data ||
        response;

      if (
        data?.statuscode &&
        data.statuscode !== "00"
      ) {
        throw new Error(
          data?.statusmessage ||
            data?.message ||
            "Unable to resend OTP"
        );
      }

      toast.success(
        data?.statusmessage ||
          data?.message ||
          "A new verification code has been sent."
      );

    } catch (error) {
      console.error(
        "Resend OTP error:",
        error
      );

      toast.error(
        error?.response?.data
          ?.statusmessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to resend verification code"
      );

    } finally {
      setResendLoading(false);
    }
  };

  /*
   * ============================
   * LOGIN FORM
   * ============================
   */

  const signInForm = useFormik({
    initialValues: {
      emailAddress: "",
      userPassword: "",
    },

    validationSchema: Yup.object({
      emailAddress: Yup.string()
        .email(
          "Please provide a valid email address"
        )
        .required(
          "Please provide your email address"
        ),

      userPassword: Yup.string().required(
        "Password cannot be empty"
      ),
    }),

    onSubmit: async (
      values,
      { resetForm }
    ) => {
      try {
        const {
          emailAddress,
          userPassword,
        } = values;

        const signInData = {
          email: emailAddress,
          password: userPassword,
        };

        /*
         * Authenticate email/password.
         */
        const { payload } =
          await dispatch(
            userAuthenticate(signInData)
          );

        if (!payload) {
          toast.error(
            "Unable to sign in. Please try again."
          );
          return;
        }

        /*
         * Determine where to send the user
         * after authentication.
         */
        const redirectPath =
          location.state?.actionType ===
          "saveVpsActionOnAuthClick"
            ? "/client-area/vps"
            : "/client-area";

        /*
         * ============================
         * 2FA REQUIRED
         * ============================
         *
         * Backend returns:
         *
         * {
         *   statuscode: "00",
         *   otp_required: true,
         *   method: "email",
         *   email: "..."
         * }
         */

        if (
          payload?.statuscode === "00" &&
          payload?.otp_required === true
        ) {
          setPendingRedirect(
            redirectPath
          );

          setOtpMethod(
            payload?.method || ""
          );

          setOtpEmail(
            payload?.email ||
              emailAddress
          );

          setOtpCode("");
          setRequiresOtp(true);

          /*
           * Do NOT load projects or navigate.
           * User must verify OTP first.
           */
          return;
        }

        /*
         * ============================
         * NORMAL LOGIN
         * ============================
         */

        if (
          payload?.statuscode === "00"
        ) {
          resetForm();

          await completeLogin(
            redirectPath
          );

          return;
        }

        /*
         * Backend returned an error response.
         */
        toast.error(
          payload?.statusmessage ||
            payload?.message ||
            "Unable to sign in"
        );

      } catch (error) {
        console.error(
          "Login error:",
          error
        );

        toast.error(
          error?.response?.data
            ?.statusmessage ||
            error?.response?.data
              ?.message ||
            error?.message ||
            "Unable to sign in"
        );
      }
    },
  });

  /*
   * ============================
   * BACK TO LOGIN
   * ============================
   */

  const handleBackToLogin = () => {
    setRequiresOtp(false);
    setOtpCode("");
    setOtpEmail("");
    setOtpMethod("");
  };

  return (
    <div className="px-4 flex justify-center items-center py-8">
      <div
        ref={formRef}
        className={`my-3 w-full md:w-[50%] lg:w-[40%] ${
          formVisible
            ? "animate-reveal"
            : "opacity-0"
        }`}
      >

        {/* ========================= */}
        {/* NORMAL LOGIN */}
        {/* ========================= */}

        {!requiresOtp ? (
          <form
            onSubmit={
              signInForm.handleSubmit
            }
          >
            <div className="bg-white dark:bg-[#141D26] border border-[#c4c4c432] dark:text-white rounded-md px-6 py-10 grid gap-6">

              <div>
                <p className="pb-1 crimson-font text-lg font-semibold">
                  Sign in
                </p>

                <p className="text-xs text-[#c4c4c4] pb-2 border-b dark:border-b-[#c4c4c432]">
                  This page is restricted
                </p>
              </div>

              {/* EMAIL */}

              <Input
                label="Email address"
                type="email"
                name="emailAddress"
                value={
                  signInForm.values
                    .emailAddress
                }
                onChange={
                  signInForm.handleChange
                }
                onBlur={
                  signInForm.handleBlur
                }
                onError={
                  signInForm.touched
                    .emailAddress &&
                  signInForm.errors
                    .emailAddress
                    ? signInForm.errors
                        .emailAddress
                    : null
                }
              />

              {/* PASSWORD */}

              <div>
                <Input
                  label="Password"
                  type="password"
                  isPassword="true"
                  name="userPassword"
                  value={
                    signInForm.values
                      .userPassword
                  }
                  onChange={
                    signInForm.handleChange
                  }
                  onBlur={
                    signInForm.handleBlur
                  }
                  onError={
                    signInForm.touched
                      .userPassword &&
                    signInForm.errors
                      .userPassword
                      ? signInForm.errors
                          .userPassword
                      : null
                  }
                />

                <Link
                  to="/password-reset-link"
                  className="text-sm md:text-xs text-[#1DA1F2] cursor-pointer"
                >
                  Forgot password?
                </Link>
              </div>

              {/* ACTIONS */}

              <div className="grid md:flex md:justify-between gap-6 md:gap-0 items-center">

                <Button
                  buttonRole="submit"
                  loading={loading}
                  buttonText="Sign in"
                />

                <Link
                  to="/create-account"
                  className="text-sm md:text-xs text-center opacity-70 hover:text-[#1DA1F2] cursor-pointer"
                >
                  Create an account
                </Link>

              </div>

            </div>
          </form>
        ) : (

          /* ========================= */
          /* OTP VERIFICATION */
          /* ========================= */

          <form
            onSubmit={
              handleVerifyOtp
            }
          >
            <div className="bg-white dark:bg-[#141D26] border border-[#c4c4c432] dark:text-white rounded-md px-6 py-10 grid gap-6">

              <div>
                <p className="pb-1 crimson-font text-lg font-semibold">
                  Verify your sign in
                </p>

                {otpMethod === "email" ? (
                  <>
                    <p className="text-xs text-[#c4c4c4]">
                      A verification code
                      has been sent to your
                      registered email
                      address.
                    </p>

                    {otpEmail && (
                      <p className="text-sm mt-2 font-medium">
                        {otpEmail}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-[#c4c4c4]">
                    Enter your
                    authentication code to
                    continue.
                  </p>
                )}
              </div>

              {/* OTP */}

              <Input
            label="Verification code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            name="otpCode"
            value={otpCode}
            onChange={(e) => {
              const value =
                e.target.value.replace(/\D/g, "");

              setOtpCode(value);
            }}
            placeholder="Enter OTP"
          />

              {/* VERIFY */}

              <Button
                buttonRole="submit"
                loading={otpLoading}
                buttonText="Verify"
              />

              <div className="grid gap-3">

                {/* Only show resend for Email OTP */}

                {otpMethod === "email" && (
                  <button
                    type="button"
                    disabled={
                      resendLoading
                    }
                    onClick={
                      handleResendOtp
                    }
                    className="text-sm text-primaryBlue text-left disabled:opacity-50"
                  >
                    {resendLoading
                      ? "Sending..."
                      : "Didn't receive the code? Resend OTP"}
                  </button>
                )}

                <button
                  type="button"
                  onClick={
                    handleBackToLogin
                  }
                  className="text-sm text-gray-500 text-left"
                >
                  Back to sign in
                </button>

              </div>

            </div>
          </form>
        )}

      </div>
    </div>
  );
};

export default SignIn;